import { GenericAbortSignal } from 'axios';

import FilterValueArray from 'models/filterValues/FilterValueArray';
import FilterValueBoolean from 'models/filterValues/FilterValueBoolean';
import JobProcess from 'models/JobProcess';
import { Note } from 'models/Notes';
import { Paginated } from 'models/Paginated';
import { ReviewOptions, ReviewStatus, ReviewType } from 'models/Review';
import ReviewModelFactory from 'models/reviews/ReviewModelFactory';
import { CreateParams as DischargeParams } from 'services/api/locationEpisodeDischarges';
import { BaseMutationParams } from 'services/api/types';
import { BaseIndexQueryParams } from 'services/api/types';
import http from 'services/http';
import { FilterState } from 'stores/types';

export const referralQueryKeys = {
  base: ['referrals'],
  index: (params: Record<string, any> = {}) => {
    const locationEpisodeId = params.locationEpisodeId || params.filters?.locationEpisodeId;
    return ['referrals', 'index', { ...params, locationEpisodeId }];
  },
  show: (params: Record<string, any> = {}) => ['referrals', 'show', params],
};

export const referralQueryParams = {
  show: (id: string) => ({
    id,
    include: [
      'attachments.blob',
      'attachments.createdByUser',
      'attachments.createdByGroup',
      'attachments.extractDataAIJob',
      'episode',
      'group.healthPlanClassificationIds',
      'locationEpisode.group.groupType',
      'locationEpisode.owner.client.clientGroupTypes',
      'locationEpisode.owner.client.enabledProviderTypes',
      'locationEpisode.reviews.group.groupType',
      'summaryJobForCurrentAttachments',
    ].join(','),
  }),
};

export const authorizationQueryKeys = {
  base: ['authorizations'],
  index: (params: Record<string, any> = {}) => {
    const locationEpisodeId = params.locationEpisodeId || params.filters?.locationEpisodeId;
    return ['authorizations', 'index', { ...params, locationEpisodeId }];
  },
  show: (params: Record<string, any> = {}) => ['authorizations', 'show', params],
};

export const authorizationQueryParams = {
  show: (id: string) => ({
    id,
    include: [
      'attachments.blob',
      'attachments.createdByUser',
      'attachments.createdByGroup',
      'attachments.extractDataAIJob',
      'previousAuthorizationReview.summaryJobForCurrentAttachments',
      'summaryJobForCurrentAttachments',
      'comparisonJobForCurrentSummary',
    ].join(','),
  }),
};

type CreateProjectedDischargeInput = {
  locationEpisodeId: string;
  type: ReviewType;
  projectedDischargeDate: string;
  planningDischargeTo?: string | null;
  status: ReviewStatus;
} & BaseMutationParams;

type CreateServiceRefusalInput = {
  locationEpisodeId: string;
  type: ReviewType;
  note?: Note;
  refusedBy: {
    id: string;
    name: string;
    type: string;
  };
} & BaseMutationParams;

type CreateAltcsApplicationInput = {
  type: ReviewType;
  locationEpisodeId: string;
} & BaseMutationParams;

type CreateRequestSafeReturnInput = {
  type: ReviewType;
  locationEpisodeId: string;
  note?: Note;
  groupId: string;
} & BaseMutationParams;

type CreateReferralInput = {
  type: ReviewType.REFERRAL;
  locationEpisodeId: string;
  groupId: string;
  note?: Note;
  previousReviewId?: string;
  removedAttachmentIds?: string[];
  expectedDischargeDate?: string;
  address1?: string;
  address2?: string;
  city?: string;
  state?: string;
  zip?: string;
  phoneNumber?: string;
} & BaseMutationParams;

export type CreateReviewInput =
  | CreateProjectedDischargeInput
  | CreateServiceRefusalInput
  | CreateAltcsApplicationInput
  | CreateRequestSafeReturnInput
  | CreateReferralInput;

export async function createReview({ locationEpisodeId, ...params }: CreateReviewInput) {
  return http
    .post<ReviewOptions>(`/location_episodes/${locationEpisodeId}/reviews`, params)
    .then((response) => ReviewModelFactory.get(response.data));
}

type UpdateProjectedDischargeReviewInput = {
  id: string;
  projectedDischargeDate: string;
  planningDischargeTo?: string | null;
  status: ReviewStatus;
} & BaseMutationParams;

type UpdateAuthorizationReviewInput = {
  id: string;
  approvedThroughDate?: string | null;
  nextReviewDate?: string | null;
  validThroughDate?: string | null;
  authorizationNumber?: string;
  note?: Note;
  attachmentIds?: string[];
} & BaseMutationParams;

type UpdateServiceRefusalReviewInput = {
  id: string;
  type: ReviewType.SERVICE_REFUSAL;
  reassignedTo?: {
    id: string;
    name: string;
  };
  note?: Note;
  status: ReviewStatus;
} & BaseMutationParams;

type UpdateAltcsApplicationReviewInput = Partial<DischargeParams> & {
  id: string;
  status: ReviewStatus;
  ltcGroupId?: string;
  providerId?: string;
} & BaseMutationParams;

type UpdateSafeReturnReviewInput = {
  id: string;
  status: ReviewStatus;
  note?: Note;
  reviewedById: string;
} & BaseMutationParams;

type UpdateReferralReviewInput = {
  id: string;
  status: ReviewStatus;
  note?: Note;
  denialReasonApiName?: string;
} & BaseMutationParams;

export type UpdateReviewInput =
  | UpdateProjectedDischargeReviewInput
  | UpdateAuthorizationReviewInput
  | UpdateServiceRefusalReviewInput
  | UpdateAltcsApplicationReviewInput
  | UpdateSafeReturnReviewInput
  | UpdateReferralReviewInput;

export async function updateReview({ id, ...params }: UpdateReviewInput) {
  return http.put<ReviewOptions>(`/reviews/${id}`, params).then((response) => ReviewModelFactory.get(response.data));
}

type ShowParams = {
  id: string;
  include?: string;
};
export async function showReview({ id, ...params }: ShowParams) {
  return http
    .get<ReviewOptions>(`/reviews/${id}`, { params })
    .then((response) => ReviewModelFactory.get(response.data));
}

export async function createReviewExport(id: string) {
  return http.post<JobProcess>(`/reviews/${id}/exports`).then((res) => new JobProcess(res.data));
}

export async function createReviewAttachmentsExport(reviewId: string, attachmentIds?: string[]) {
  return http
    .post<JobProcess>(`/reviews/${reviewId}/attachments_export`, { attachmentIds })
    .then((res) => new JobProcess(res.data));
}

export type ReviewsIndexParams = {
  filters: FilterState<FilterValueArray>;
  type?: string;
} & BaseIndexQueryParams;

function serializeFilters(filters: Record<string, any>): Record<string, string> {
  const result: Record<string, string> = {};

  Object.entries(filters).forEach(([key, value]) => {
    if (key === 'createdAt') {
      result[key] = `[${value[0].toISOString()},${value[1].toISOString()}]`;
    } else if (value instanceof Date) {
      // Format date as ISO string
      result[key] = value.toISOString();
    } else if (value instanceof FilterValueBoolean) {
      if (value.value) {
        result[key] = value.value.toString();
      }
    } else if (Array.isArray(value)) {
      // Handle array values
      result[key] = value.map((item) => item.id || item.toString()).join(',');
    } else if (value && typeof value === 'object') {
      // Handle FilterValueArray
      result[key] = (value as FilterValueArray).serialize().join(',');
    } else {
      result[key] = value;
    }
  });

  return result;
}

export async function indexReviews({ filters, ...params }: ReviewsIndexParams, signal: GenericAbortSignal) {
  return http
    .get<Paginated<ReviewOptions>>(`reviews`, { params: { ...params, ...serializeFilters(filters) }, signal })
    .then((response) => ({
      ...response.data,
      data: response.data.data.map((x) => ReviewModelFactory.get(x)),
    }));
}
